import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, TrendingUp, AlertCircle, X, Search, Sparkles, Megaphone, Gauge, Shield, Lightbulb } from 'lucide-react';
import { getMyDiagnostics } from '../../api/diagnosticsApi';
import { DiagnosticsResponse } from '../../types/diagnostics';
import Spinner from '../../components/Spinner';
import AvatarIndicator from '../../components/AvatarIndicator';
import BookingStatsCard from '../../components/booking/BookingStatsCard';

export default function DiagnosticsOverview() {
  const navigate = useNavigate();
  const [latestDiagnostic, setLatestDiagnostic] = useState<DiagnosticsResponse | null>(null);
  const [recentDiagnostics, setRecentDiagnostics] = useState<DiagnosticsResponse[]>([]);
  const [selectedDiagnostic, setSelectedDiagnostic] = useState<DiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getMyDiagnostics();

        if (Array.isArray(result) && result.length > 0) {
          const mappedResult = result.map((d: any) => {
            const parsedScores = d.categoryScoresJson
              ? JSON.parse(d.categoryScoresJson)
              : [];

            return {
              ...d,

              categoryScores: parsedScores.map((s: any) => ({
                category: s.Category || s.category,
                percentage: s.Percentage || s.percentage,
                issues: s.Issues || s.issues || [],
              })),

              summaryReport: d.summaryReportJson
                ? JSON.parse(d.summaryReportJson)
                : [],

              recommendations: d.recommendationsJson
                ? JSON.parse(d.recommendationsJson)
                : [],

              ctaServices: d.ctaServicesJson
                ? JSON.parse(d.ctaServicesJson)
                : [],
            };
          });

          const sortedDiagnostics = mappedResult.sort(
            (a, b) =>
              new Date(b.createdDate).getTime() -
              new Date(a.createdDate).getTime()
          );

          setLatestDiagnostic(sortedDiagnostics[0]);
          setRecentDiagnostics(sortedDiagnostics);
        } else {
          setLatestDiagnostic(null);
          setRecentDiagnostics([]);
        }
      } catch {
        setError(
          'Failed to load dashboard data. Please try again later.'
        );

        setLatestDiagnostic(null);
        setRecentDiagnostics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate total issues for the LATEST diagnostic
  const totalIssues =
    latestDiagnostic?.categoryScores?.reduce(
      (total, category) =>
        total + (category.issues?.length ?? 0),
      0
    ) ?? 0;

  const formatCreatedDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Spinner size="h-12 w-12" />
        <p className="mt-4 text-slate-500 animate-pulse font-medium">Gathering your latest metrics...</p>
      </div>
    );
  }

  function DiagnosticAction({
    icon: Icon,
    label,
    onClick,
  }: {
    icon: typeof Search;
    label: string;
    onClick: () => void;
  }) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-strivre-softgrey"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-strivre-lightburgundy text-strivre-burgundy">
          <Icon size={18} />
        </span>

        <span className="flex-1 text-sm font-semibold text-strivre-darktext group-hover:text-strivre-burgundy">
          {label}
        </span>

        <span className="text-strivre-mutedtext group-hover:text-strivre-burgundy">
          ›
        </span>
      </button>
    );
  }

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      {/* Hero Section */}
      <header className="mt-5 mb-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold leading-tight text-slate-800">
            <Gauge
              className="text-strivre-burgundy"
              size={24}
            />
            Diagnostics Overview
          </h1>

          <p className="mt-1 text-left text-sm text-slate-500">
            Monitor diagnostic activity, review recent website analyses, and launch assessments across the Strivre diagnostic suite.
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate('/diagnostics/website-tool')
          }
          className="btn-primary btn-md inline-flex items-center gap-2 self-start"
        >
          <Search size={17} />
          Run Website Diagnostic
        </button>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-lg flex items-center justify-between animate-shake">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} />
            <span className="font-medium">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-xl font-bold">×</button>
        </div>
      )}

      {/* Stats */}
      {latestDiagnostic && (
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <BookingStatsCard
            title="Latest Score"
            value={`${latestDiagnostic.overallPercentage}%`}
            icon={<Gauge size={20} />}
          />

          <BookingStatsCard
            title="Latest Grade"
            value={latestDiagnostic.grade ?? 'N/A'}
            icon={<Activity size={20} />}
          />

          <BookingStatsCard
            title="Issues Found"
            value={totalIssues}
            icon={<AlertCircle size={20} />}
            highlight={totalIssues > 0}
          />

          <BookingStatsCard
            title="Website Analyses"
            value={recentDiagnostics.length}
            icon={<TrendingUp size={20} />}
          />
        </div>
      )}

      {/* Latest Website Diagnostic */}
      {latestDiagnostic && (
        <section className="mb-6 overflow-hidden rounded-lg border border-strivre-lightnavy bg-white">
          <div className="flex flex-col gap-4 border-b border-strivre-lightnavy px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 text-left">
              <p className="text-xs font-bold uppercase tracking-wide text-strivre-burgundy">
                Latest Website Diagnostic
              </p>

              <h2 className="mt-1 truncate text-lg font-bold text-strivre-navy">
                {latestDiagnostic.url}
              </h2>

              <p className="mt-1 text-xs text-strivre-mutedtext">
                Analyzed {formatCreatedDate(latestDiagnostic.createdDate)}
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                setSelectedDiagnostic(latestDiagnostic)
              }
              className="btn-secondary btn-md shrink-0"
            >
              View Report
            </button>
          </div>

          <div className="p-6">
            {/* Overall result */}
            <div className="mb-6 flex flex-col gap-4 rounded-lg border border-strivre-lightnavy bg-strivre-softgrey p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-left">
                <p className="text-xs font-semibold uppercase tracking-wide text-strivre-mutedtext">
                  Overall Website Health
                </p>

                <div className="mt-2 flex items-center gap-3">
                  <span className="text-3xl font-bold text-strivre-navy">
                    {latestDiagnostic.overallPercentage}%
                  </span>

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      latestDiagnostic.overallPercentage >= 80
                        ? 'bg-emerald-50 text-emerald-700'
                        : latestDiagnostic.overallPercentage >= 60
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {latestDiagnostic.grade}
                  </span>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <p className="text-xs font-semibold uppercase tracking-wide text-strivre-mutedtext">
                  Issues Found
                </p>

                <p className="mt-2 text-2xl font-bold text-strivre-navy">
                  {totalIssues}
                </p>
              </div>
            </div>

            {/* Category scores */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {(latestDiagnostic.categoryScores ?? []).map((category) => {
                const percentage = Math.round(
                  category.percentage ?? 0
                );

                return (
                  <div
                    key={category.category}
                    className="rounded-lg border border-strivre-lightnavy bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold leading-snug text-strivre-darktext">
                        {category.category}
                      </p>

                      <span className="shrink-0 text-lg font-bold text-strivre-navy">
                        {percentage}%
                      </span>
                    </div>

                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-strivre-softgrey">
                      <div
                        className="h-full rounded-full bg-strivre-burgundy transition-all duration-500"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, percentage)
                          )}%`,
                        }}
                      />
                    </div>

                    <p className="mt-3 text-xs text-strivre-mutedtext">
                      {category.issues?.length ?? 0}{' '}
                      {(category.issues?.length ?? 0) === 1
                        ? 'issue'
                        : 'issues'}{' '}
                      found
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Recent Website Analyses Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-lg border border-strivre-lightnavy bg-white lg:col-span-2">
          <div className="flex items-center justify-between border-b border-strivre-lightnavy px-6 py-5">
            <div className="text-left">
              <h2 className="text-lg font-bold text-strivre-navy">
                Recent Website Diagnostics
              </h2>

              <p className="mt-1 text-xs text-strivre-mutedtext">
                Your most recently completed website analyses.
              </p>
            </div>

            <span className="rounded-full bg-strivre-softgrey px-3 py-1 text-xs font-semibold text-strivre-mutedtext">
              {recentDiagnostics.length} total
            </span>
          </div>

          {recentDiagnostics.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {recentDiagnostics
                .slice(0, 5)
                .map((diagnostic) => (
                  <button
                    key={diagnostic.id}
                    type="button"
                    onClick={() =>
                      setSelectedDiagnostic(diagnostic)
                    }
                    className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-strivre-softgrey"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-strivre-navy">
                        {diagnostic.url}
                      </p>

                      <p className="mt-1 text-xs text-strivre-mutedtext">
                        {formatCreatedDate(diagnostic.createdDate)}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-sm font-bold text-strivre-navy">
                        {diagnostic.overallPercentage}%
                      </span>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          diagnostic.overallPercentage >= 80
                            ? 'bg-emerald-50 text-emerald-700'
                            : diagnostic.overallPercentage >= 60
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {diagnostic.grade}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          ) : (
            <div className="p-10 text-center">
              <Search
                size={32}
                className="mx-auto text-slate-300"
              />

              <p className="mt-3 text-sm text-slate-500">
                No website diagnostics have been completed yet.
              </p>
            </div>
          )}
        </section>

        {/* Quick Actions */}
        <section className="h-fit overflow-hidden rounded-lg border border-strivre-lightnavy bg-white">
          <div className="border-b border-strivre-lightnavy px-6 py-5 text-left">
            <h2 className="text-lg font-bold text-strivre-navy">
              Run a Diagnostic
            </h2>

            <p className="mt-1 text-xs text-strivre-mutedtext">
              Choose an assessment to begin.
            </p>
          </div>

          <div className="space-y-2 p-4">
            <DiagnosticAction
              icon={Search}
              label="Website Diagnostic"
              onClick={() =>
                navigate('/diagnostics/website-tool')
              }
            />

            <DiagnosticAction
              icon={Megaphone}
              label="Marketing Diagnostic"
              onClick={() =>
                navigate('/diagnostics/marketing-tool')
              }
            />

            <DiagnosticAction
              icon={Activity}
              label="Business Health & Strategy"
              onClick={() =>
                navigate(
                  '/diagnostics/business-assessment'
                )
              }
            />

            <DiagnosticAction
              icon={Sparkles}
              label="Talent Acquisition"
              onClick={() =>
                navigate(
                  '/diagnostics/talent-diagnostics'
                )
              }
            />
            <DiagnosticAction
              icon={Sparkles}
              label="Recruitment Diagnostics"
              onClick={() =>
                navigate(
                  '/diagnostics/recruitment-diagnostics'
                )
              }
            />
          </div>
        </section>
      </div>

      {/* Results Modal */}
      {selectedDiagnostic && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 md:p-8">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setSelectedDiagnostic(null)}
            />
          <div className="relative flex max-h-full w-full max-w-8xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-strivre-lightnavy bg-white p-5 md:px-8 md:py-6">
              <div>
                <h2 className="text-xl font-bold text-strivre-navy">Diagnostic Report</h2>
                <p className="text-sm text-slate-500">{selectedDiagnostic.url}</p>
                <p className="mt-1 text-xs text-slate-400">Analyzed {formatCreatedDate(selectedDiagnostic.createdDate)}</p>
              </div>
              <button 
                onClick={() => setSelectedDiagnostic(null)}
                className="p-2 hover:bg-strivre-softgrey rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X size={24} className="text-slate-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              <div className="space-y-8">
                {/* 1. Overall Health + Category Scores */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                  {/* Main Visualizer Card */}
                  <div className="group relative flex min-h-[300px] flex-row items-center justify-center gap-8 overflow-hidden rounded-3xl border border-strivre-lightnavy bg-white p-8 shadow-sm lg:col-span-5">
                    <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

                    <div className="flex-shrink-0">
                      <AvatarIndicator
                        state={selectedDiagnostic.visualIndicator}
                      />
                    </div>

                    <div className="flex flex-col items-center md:items-start">
                      <div className="rounded-[2.5rem] border border-slate-100 bg-slate-50/50 px-8 py-6 shadow-inner backdrop-blur-sm transition-all group-hover:bg-white group-hover:shadow-md">
                        <span className="block text-7xl font-black leading-none tracking-tighter text-slate-900">
                          {(selectedDiagnostic.overallPercentage ?? 0).toFixed(2)}

                          <span className="ml-1 text-3xl font-bold text-indigo-600">
                            %
                          </span>
                        </span>

                        <span className="mt-3 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
                          Overall Health
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Metric Tiles */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
                    {(selectedDiagnostic.categoryScores ?? []).map((category, index) => {
                      const icons = [
                        Sparkles,
                        Megaphone,
                        Gauge,
                        Shield,
                      ];

                      const colors = [
                        '#4f46e5',
                        '#db2777',
                        '#10b981',
                        '#ea580c',
                      ];

                      const Icon =
                        icons[index % icons.length];

                      const color =
                        colors[index % colors.length];

                      return (
                        <div
                          key={category.category}
                          className="group flex flex-col justify-between rounded-3xl border border-slate-100 bg-white p-6 shadow-sm transition-all hover:border-blue-200"
                        >
                          <div className="mb-4 flex items-start justify-between">
                            <div
                              className="rounded-2xl border border-transparent bg-slate-50 p-3 transition-colors group-hover:border-slate-100 group-hover:bg-white"
                              style={{ color }}
                            >
                              <Icon size={24} />
                            </div>

                            <span
                              className="text-4xl font-black tracking-tighter"
                              style={{ color }}
                            >
                              {(category.percentage ?? 0).toFixed(2)}%
                            </span>
                          </div>

                          <div>
                            <h4 className="mb-2 text-sm font-bold uppercase tracking-tight text-slate-800">
                              {category.category}
                            </h4>

                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-strivre-softgrey">
                              <div
                                className="h-full rounded-full transition-all duration-1000"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    Math.max(
                                      0,
                                      category.percentage ?? 0
                                    )
                                  )}%`,
                                  backgroundColor: color,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Key Improvement Opportunities */}
                <div className="rounded-3xl border border-strivre-lightburgundy bg-strivre-lightburgundy p-8 text-left shadow-sm">
                  <h4 className="mb-6 flex items-center gap-3 text-xl font-bold text-slate-900">
                    <div className="rounded-lg bg-strivre-burgundy p-2 text-white">
                      <Lightbulb size={20} />
                    </div>

                    Key Improvement Opportunities
                  </h4>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {(selectedDiagnostic.recommendations ?? []).map(
                      (recommendation, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-4 rounded-2xl border border-indigo-50 bg-white p-5 shadow-sm"
                        >
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-strivre-burgundy text-sm font-bold text-white">
                            {index + 1}
                          </span>

                          <p
                            className="text-left text-sm font-normal leading-relaxed text-slate-700"
                            dangerouslySetInnerHTML={{
                              __html: recommendation,
                            }}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* 3. Website Services CTA */}
                <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-blue-600 to-indigo-800 p-12 text-center text-white shadow-2xl">
                  <div className="relative z-10">
                    <h3 className="mb-4 text-3xl font-bold tracking-tight text-white">
                      Ready to Take Your Website Further?
                    </h3>

                    <p className="mx-auto mb-8 max-w-2xl text-lg font-normal leading-relaxed text-blue-100">
                      Whether you want a faster website, stronger branding,
                      or a smoother customer experience, we're here to help
                      turn insights into real improvements.
                    </p>

                    <button
                      type="button"
                      className="rounded-full bg-white px-10 py-4 font-bold text-blue-700 transition-all hover:scale-105 hover:shadow-xl active:scale-95"
                    >
                      Contact Us
                    </button>
                  </div>
                </div>

                {/* 4. Report Summary */}
                <div className="relative rounded-3xl border border-strivre-lightnavy bg-white p-10 text-left shadow-sm">
                  <h3 className="mb-8 flex items-center gap-3 text-2xl font-bold text-slate-900">
                    <Megaphone
                      size={22}
                      className="text-indigo-600"
                    />

                    Report Summary
                  </h3>

                  <div className="ml-2 space-y-6 border-l-4 border-indigo-100 pl-8 text-left">
                    {(selectedDiagnostic.summaryReport ?? []).map(
                      (paragraph, index) => (
                        <p
                          key={index}
                          className="text-md font-normal leading-relaxed text-slate-600"
                          dangerouslySetInnerHTML={{
                            __html: paragraph,
                          }}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 md:p-6 border-t bg-white flex justify-end">
              <button 
                onClick={() => setSelectedDiagnostic(null)}
                className="btn-secondary px-6 py-2"
              >
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}